import { freeToolHref, getFooterFreeTools } from '@/lib/free-tools/registry';
import { COMPARE_NAV, type NavLinkItem } from '@/lib/marketing-nav';

/**
 * Mega-footer config. Each column is a curated slice of an existing surface
 * (pSEO programs in `content/pseo-programs/`, interactive tools, persona hubs,
 * legal pages). The full pSEO inventory still reaches users via the "All X →"
 * hub link + sitemap — the column lists are the high-traffic / ICP-aligned
 * subset we want on every page for internal-linking + brand impression.
 *
 * Adding/removing slugs: edit the arrays below. Every label must point at a
 * route that already exists in `src/app/` or a published pSEO entity — the
 * footer is not an aspirational backlog.
 *
 * Voice: outcome-led, no hyperbole. Trim leading "Best " / "Free " when the
 * column heading already implies it; keep "Wisprs vs X" casing.
 */

export interface FooterColumn {
  /** Column heading; also the mobile <details> summary. English source. */
  heading: string;
  /** Catalog key for the translated heading. See `src/lib/ui-copy.ts`. */
  headingKey?: string;
  links: NavLinkItem[];
  /** Optional "View all →" link rendered at the bottom of the column. */
  hub?: NavLinkItem;
}

const FREE_TOOL_COLUMN_LINKS: NavLinkItem[] = [
  // Opt-in via `surfaces.footer` in the registry, replacing a positional
  // `FREE_TOOL_APP_LINKS.slice(0, 7)` whose meaning changed on every reorder.
  ...getFooterFreeTools().map((tool) => ({
    href: freeToolHref(tool.slug),
    label: tool.label,
    prefetch: false,
  })),
  // pSEO entities from `content/pseo-programs/free-tools.json`, not registry members.
  { href: '/tools/free-mp3-to-text', label: 'Free MP3 to Text', prefetch: false },
  { href: '/tools/free-podcast-transcript', label: 'Free Podcast Transcript', prefetch: false },
];

const USE_CASE_LINKS: NavLinkItem[] = [
  { href: '/podcast/podcast-transcription-service', label: 'Podcast transcription', prefetch: false },
  { href: '/use-cases/meeting-transcription-software', label: 'Meeting transcription', prefetch: false },
  { href: '/use-cases/sales-call-transcription', label: 'Sales call transcription', prefetch: false },
  { href: '/use-cases/legal-transcription-software', label: 'Legal transcription', prefetch: false },
  { href: '/use-cases/medical-transcription-service', label: 'Medical transcription', prefetch: false },
  { href: '/use-cases/lecture-transcription-service', label: 'Lecture transcription', prefetch: false },
  { href: '/use-cases/webinar-transcription-service', label: 'Webinar transcription', prefetch: false },
  { href: '/use-cases/research-interview-transcription', label: 'Research interviews', prefetch: false },
  { href: '/use-cases/voicemail-transcription', label: 'Voicemail transcription', prefetch: false },
  { href: '/use-cases/mp3-transcription', label: 'MP3 transcription', prefetch: false },
];

const COMPARE_LINKS: NavLinkItem[] = [
  { href: '/alternatives/wisprs-vs-otter-ai', label: 'Wisprs vs Otter.ai', prefetch: false },
  { href: '/alternatives/wisprs-vs-descript', label: 'Wisprs vs Descript', prefetch: false },
  { href: '/alternatives/wisprs-vs-turboscribe', label: 'Wisprs vs TurboScribe', prefetch: false },
  { href: '/alternatives/wisprs-vs-rev', label: 'Wisprs vs Rev', prefetch: false },
  { href: '/alternatives/wisprs-vs-fireflies', label: 'Wisprs vs Fireflies', prefetch: false },
  { href: '/alternatives/wisprs-vs-temi', label: 'Wisprs vs Temi', prefetch: false },
  { href: '/alternatives/best-otter-ai-alternatives', label: 'Otter.ai alternatives', prefetch: false },
  { href: '/alternatives/descript-alternatives', label: 'Descript alternatives', prefetch: false },
  { href: '/alternatives/best-transcription-software', label: 'Best transcription software', prefetch: false },
  { href: '/alternatives/best-video-transcription-software', label: 'Best video transcription', prefetch: false },
  { href: '/alternatives/best-ai-transcription', label: 'Best AI transcription', prefetch: false },
  { href: '/alternatives/best-dictation-software', label: 'Best dictation software', prefetch: false },
];

const LANGUAGE_LINKS: NavLinkItem[] = [
  { href: '/speech-to-text/spanish', label: 'Spanish', prefetch: false },
  { href: '/speech-to-text/french', label: 'French', prefetch: false },
  { href: '/speech-to-text/german', label: 'German', prefetch: false },
  { href: '/speech-to-text/portuguese', label: 'Portuguese', prefetch: false },
  { href: '/speech-to-text/italian', label: 'Italian', prefetch: false },
  { href: '/speech-to-text/mandarin', label: 'Mandarin', prefetch: false },
  { href: '/speech-to-text/japanese', label: 'Japanese', prefetch: false },
  { href: '/speech-to-text/korean', label: 'Korean', prefetch: false },
  { href: '/speech-to-text/hindi', label: 'Hindi', prefetch: false },
  { href: '/speech-to-text/arabic', label: 'Arabic', prefetch: false },
  { href: '/speech-to-text/russian', label: 'Russian', prefetch: false },
  { href: '/speech-to-text/dutch', label: 'Dutch', prefetch: false },
];

const PERSONA_LINKS: NavLinkItem[] = [
  { href: '/creators', label: 'For creators', messageKey: 'ui.footer.links.forCreators', prefetch: false },
  { href: '/podcast', label: 'For podcasters', messageKey: 'ui.footer.links.forPodcasters', prefetch: false },
  { href: '/teams', label: 'For teams', messageKey: 'ui.footer.links.forTeams', prefetch: false },
  { href: '/sales', label: 'For sales teams', messageKey: 'ui.footer.links.forSales', prefetch: false },
  { href: '/enterprise', label: 'For enterprise', messageKey: 'ui.footer.links.forEnterprise', prefetch: false },
];

const PRODUCT_LINKS: NavLinkItem[] = [
  { href: '/features', label: 'Features', messageKey: 'ui.footer.links.features' },
  { href: '/pricing', label: 'Pricing', messageKey: 'ui.footer.links.pricing' },
  { href: '/developer', label: 'API & developers' },
  { href: '/mcp/setup', label: 'MCP setup' },
  { href: '/extension', label: 'Chrome extension' },
  { href: '/faq', label: 'FAQs', messageKey: 'ui.footer.links.faq' },
  { href: '/changelog', label: 'Changelog', messageKey: 'ui.footer.links.changelog' },
  { href: '/demo', label: 'Demo', messageKey: 'ui.footer.links.demo' },
  { href: '/stats', label: 'Stats', messageKey: 'ui.footer.links.stats' },
  { href: '/status', label: 'Status', messageKey: 'ui.footer.links.status' },
];

const COMPANY_LINKS: NavLinkItem[] = [
  { href: '/blog', label: 'Blog', messageKey: 'ui.footer.links.blog' },
  { href: '/about', label: 'About', messageKey: 'ui.footer.links.about' },
  { href: '/contact', label: 'Contact', messageKey: 'ui.footer.links.contact' },
  { href: '/contact-sales', label: 'Talk to sales', messageKey: 'ui.footer.links.talkToSales' },
  { href: 'mailto:tosh@getwisprs.com', label: 'tosh@getwisprs.com' },
];

const LEGAL_LINKS: NavLinkItem[] = [
  { href: '/terms', label: 'Terms of service', messageKey: 'ui.footer.links.terms' },
  { href: '/privacy', label: 'Privacy policy', messageKey: 'ui.footer.links.privacy' },
  { href: '/data-protection', label: 'Data protection', messageKey: 'ui.footer.links.dataProtection' },
  { href: '/cookie-policy', label: 'Cookie policy', messageKey: 'ui.footer.links.cookiePolicy' },
];

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Free tools',
    headingKey: 'ui.footer.headings.freeTools',
    links: FREE_TOOL_COLUMN_LINKS,
    hub: { href: '/tools', label: 'All free tools →', messageKey: 'ui.footer.hubs.allFreeTools' },
  },
  {
    heading: 'By use case',
    headingKey: 'ui.footer.headings.useCases',
    links: USE_CASE_LINKS,
    hub: { href: '/use-cases', label: 'All use cases →', messageKey: 'ui.footer.hubs.allUseCases' },
  },
  {
    heading: 'Compare',
    headingKey: 'ui.footer.headings.compare',
    links: COMPARE_LINKS,
    hub: COMPARE_NAV.hub,
  },
  {
    heading: 'Languages',
    headingKey: 'ui.footer.headings.languages',
    links: LANGUAGE_LINKS,
    hub: { href: '/speech-to-text', label: 'All languages →' },
  },
  {
    heading: 'For',
    headingKey: 'ui.footer.headings.personas',
    links: PERSONA_LINKS,
  },
  {
    heading: 'Product',
    headingKey: 'ui.footer.headings.product',
    links: PRODUCT_LINKS,
  },
  {
    heading: 'Company',
    headingKey: 'ui.footer.headings.company',
    links: COMPANY_LINKS,
  },
  {
    heading: 'Legal',
    headingKey: 'ui.footer.headings.legal',
    links: LEGAL_LINKS,
  },
];
